import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import PageTabs from '../shared/PageTabs';
import { buildCourseTabs } from '../shared/tabConfigs';
import { 
  Box, 
  Typography, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  InputAdornment
} from '@mui/material';
import { 
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { courseService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import FeedbackDetailsModal from '../common/FeedbackDetailsModal';
import EmptyState from '../common/EmptyState';

const L1Feedback = () => {
  const { id: courseId } = useParams();
  const { showToast } = useToast();
  const { user } = useUser();
  const courseTabs = buildCourseTabs(courseId, { permissions: user?.permissions || [], roleId: user?.role_id });  
  const [courseInfo, setCourseInfo] = useState(null);
  const [instances, setInstances] = useState([]);
  //const [regularQuestions, setRegularQuestions] = useState([]);
  const [feedbackData, setFeedbackData] = useState([]);
  const [totalFeedbacks, setTotalFeedbacks] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstance, setSelectedInstance] = useState('');
  const [dataInitialized, setDataInitialized] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);

  // Fetch course data and questions
  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const response = await courseService.getL1Feedback(courseId);
        setCourseInfo(response.data.data.course);
        setInstances(response.data.data.instances || []);
        //setRegularQuestions(response.data.data.regularQuestions || []);
        setDataInitialized(true);
      } catch (error) {
        console.error('Error fetching course feedback data:', error);
        showToast('Failed to load feedback data', 'error');
        setDataInitialized(true);
      }
    };
    
    fetchCourseData();
  }, [courseId, showToast]);
  
  // Fetch feedback data with pagination and filters
  const fetchFeedbackData = useCallback(async () => {
    if (!dataInitialized) return;
    try {
      const response = await courseService.getL1FeedbackList(courseId, {
        draw: 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        instanceId: selectedInstance,
        search: searchTerm
      });
      
      setFeedbackData(response.data.data);
      setTotalFeedbacks(response.data.recordsTotal);
    } catch (error) {
      console.error('Error fetching feedback data:', error);
      showToast('Failed to load feedback data', 'error');
    }
  }, [courseId, page, rowsPerPage, selectedInstance, searchTerm, showToast, dataInitialized]);
 
  // Fetch feedback data when filters change
  useEffect(() => {
    if (dataInitialized) {
      fetchFeedbackData();
    }
  }, [fetchFeedbackData, dataInitialized]);
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  
  // Handle instance filter change
  const handleInstanceChange = (event) => {
    setSelectedInstance(event.target.value);
    setPage(0);
  };
  
  // Handle view details
  const handleViewDetails = (feedbackId) => {
    setSelectedFeedbackId(feedbackId);
    setShowDetailsModal(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedFeedbackId(null);
  };

  // Handle download Excel
  const handleExportFeedback = async () => {
    try {
      const response = await courseService.exportL1Feedback(courseId, selectedInstance);
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'l1_feedback.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading feedback data:', error);
      showToast('Failed to download feedback data', 'error');
    }
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <PageTabs tabs={courseTabs} />
      <Typography variant="h4" gutterBottom className='page-title'>Feedback</Typography>      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            variant="outlined"
            size="small"
            value={searchTerm}
            className="pink-filter"
            sx={{ width: 250 }}
            onChange={handleSearchChange}
            placeholder="Learners"
            InputProps={{
              startAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {/* <FormControl variant="outlined" size="small" sx={{ width: 250 }}>
            <Select
              value={selectedInstance}
              onChange={handleInstanceChange}
              className="pink-filter"
              displayEmpty
            >
              <MenuItem value="">All Instances</MenuItem>
              {instances.map((instance) => (
                <MenuItem key={instance.id} value={instance.id}>
                  {instance.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
        </Box>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />} 
            onClick={handleExportFeedback}
          >
            Export Feedback
          </Button>
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Learner</TableCell>
              {/* <TableCell>Instance</TableCell> */}
              {/* {regularQuestions.map((question) => (
                <TableCell key={question.id}>{question.question}</TableCell>
              ))} */}
              <TableCell>Submitted Date</TableCell>
              <TableCell align="center">Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feedbackData === null ? (
              null // Don't show anything while loading
            ) : feedbackData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review): a plain line. */}
                  <EmptyState framed={false} title="No feedback found" message={null} />
                </TableCell>
              </TableRow>
            ) : (
              feedbackData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row[0]}</TableCell>
                  <TableCell>{row[1]}</TableCell>
                  {/* {regularQuestions.map((_, i) => (
                    <TableCell key={i}>{row[i + 2]}</TableCell>
                  ))} */}
                  <TableCell>{row[2]}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => handleViewDetails(row[3])}
                      sx={{ minWidth: 'auto' }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalFeedbacks}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      {/* Feedback Details Modal */}
      <FeedbackDetailsModal
        open={showDetailsModal}
        onClose={handleCloseModal}
        feedbackId={selectedFeedbackId}
        type="course"
      />
    </Box>
  );
};

export default L1Feedback;